// import packages or dependencies
import { useState, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../../../app/hooks";
import {
  createUserManagementUserAction,
  resetCreateUserAction,
  updateUserManagementAdminAction,
  updateUserManagementUserAction,
  userManagement,
} from "../../userManagementSlice";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Box,
  SvgIcon,
  Typography,
  Paper,
  useTheme,
  Fade,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  IconButton,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { makeStyles } from "@mui/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import { getCookie } from "../../../../../configs/cookie-helper/cookieHelper";

// custom styles for mui components
const useStyles = makeStyles((theme) => ({
  passwordBox: {
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
  form: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
  },
  field: {
    borderRadius: "5px",
    backgroundColor: "#F2F2F2",
    "& label.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#F2F2F2",
    },
    "& .MuiOutlinedInput-root": {
      paddingRight: theme.spacing(0),
      "& fieldset": {
        borderColor: "#F2F2F2",
        borderRadius: "5px",
      },
      "&:hover fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
    },
  },
}));

// formik validation schema for create user
const validationSchema = yup.object({
  nickname: yup.string().required("Please enter nickname."),
  username: yup.string().required("Please enter username."),
  rule: yup.string().oneOf(["user", "admin"]).required("Please choose rule."),
  password: yup
    .string()
    .min(6, "Password must have at least 6 characters.")
    .required("Please enter your new password."),
  passwordConf: yup
    .string()
    .oneOf([yup.ref("password")], "Your passwords do not match.")
    .required("Please enter your confirm password."),
});

// formik validation schema for update user
const validationSchemaUpdate = yup.object({
  nickname: yup.string().required("Please enter nickname."),
  username: yup.string().required("Please enter username."),
  rule: yup.string().oneOf(["user", "admin"]).required("Please choose rule."),
  password: yup
    .string()
    .required("Please enter password.")
    .min(6, "Password must have at least 6 characters."),
  passwordConf: yup
    .string()
    .required("Please enter password confirm.")
    .oneOf([yup.ref("password")], "Your passwords do not match."),
  oldPass: yup.string().required("Please enter old password."),
});

/**
 * @returns Handles user creation form or user update form
 */
const CreateUserForm = () => {
  // states and handlers
  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();

  const theme = useTheme();

  const dispatch = useAppDispatch();

  const username = getCookie("username");

  const {
    createUser: { actionStatus, alert, formData, type, userId },
  } = useAppSelector(userManagement);

  const [showPass, setShowPass] = useState({
    password: false,
    passwordConf: false,
    oldPass: false,
  });

  const [openButton, setOpenButton] = useState(false);

  const [isDisabled, setIsDisabled] = useState(false);

  const formik = useFormik({
    initialValues: {
      nickname: "",
      username: "",
      rule: "user",
      password: "",
      passwordConf: "",
      oldPass: "",
    },
    validationSchema:
      type === "create" ? validationSchema : validationSchemaUpdate,
    onSubmit: (values) => {
      if (type === "create") {
        dispatch(createUserManagementUserAction(values));
      } else if (type === "update") {
        if (isDisabled) {
          dispatch(
            updateUserManagementAdminAction({
              id: userId,
              oldPass: values.oldPass,
              password: values.password,
              passConf: values.passwordConf,
            })
          );
        } else {
          dispatch(
            updateUserManagementUserAction({
              id: userId,
              nickname: values.nickname,
              // username: values.username, 
              rule: values.rule,
              password: values.password,
              passConf: values.passwordConf,
            })
          );
        }
      }
    },
  });

  const formikSetValues = formik.setValues;

  const formikSetTouched = formik.setTouched;

  const formikSetFieldValue = formik.setFieldValue;

  useEffect(() => {
    if (type === "create") {
      if (
        formik.values.nickname ||
        formik.values.username ||
        formik.values.password ||
        formik.values.passwordConf
      ) {
        setOpenButton(true);
      } else {
        setOpenButton(false);
      }
    } else if (type === "update") {
      if (
        formik.values.username === username &&
        formik.values.rule === "admin"
      ) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
        formikSetFieldValue("oldPass", "none");
      }
      if (formik.values.nickname || formik.values.username) {
        setOpenButton(true);
      } else {
        setOpenButton(false);
      }
    }
  }, [formik.values, type, username, formikSetFieldValue]);

  useEffect(() => {
    formikSetValues({
      nickname: formData.nickname,
      username: formData.username,
      rule: formData.rule,
      password: formData.password,
      passwordConf: formData.passwordConf,
      oldPass: formData.oldPass,
    });
  }, [formData, formikSetValues]);

  useEffect(() => {
    if (actionStatus === "success" && alert.error === false) {
      // enqueueSnackbar(
      //   alert.message === "insert_account"
      //     ? "New user created successfuly"
      //     : alert.message,
      //   {
      //     variant: "success",
      //   }
      // );
      dispatch(resetCreateUserAction());
      setOpenButton(false);
      formikSetValues({
        nickname: "",
        username: "",
        rule: "user",
        password: "",
        passwordConf: "",
        oldPass: "",
      });
      formikSetTouched({});
    } else if (actionStatus === "rejected" || alert.error === true) {
      // enqueueSnackbar(
      //   alert.message === "username_not_unique"
      //     ? "Username should be unique"
      //     : alert.message === "not_match_old_Password"
      //     ? "Password or Old Password not match"
      //     : alert.message,
      //   {
      //     variant: "error",
      //   }
      // );
      formikSetValues((last) => ({
        nickname: last.nickname,
        username: last.username,
        rule: last.rule,
        password: last.password,
        passwordConf: last.passwordConf,
        oldPass: last.oldPass,
      }));
    }
  }, [
    actionStatus,
    dispatch,
    enqueueSnackbar,
    formikSetValues,
    formikSetTouched,
    alert,
  ]);

  // render jsx
  return (
    <Paper
      elevation={3}
      sx={{
        width: "100%",
        height: "100%",
        p: "24px 24px 0px 24px",
        borderRadius: "8px",
        overflowY: "auto",
        overflowX: "hidden",
        margin: "10px 0px",
        position: "relative",
      }}
      className={classes.passwordBox}
    >
      <IconButton
        data-testid="create_user_form_close_icon"
        sx={{
          position: "absolute",
          right: "10px",
          top: "10px",
          width: "16px",
          height: "16px",
          color: theme.palette.secondary.main,
        }}
        onClick={() => {
          formik.setValues({
            nickname: "",
            username: "",
            rule: "user",
            password: "",
            passwordConf: "",
            oldPass: "",
          });
          formik.setTouched({});
          dispatch(resetCreateUserAction());
        }}
      >
        <CloseIcon data-testid="create_user_form_close_icon_svg" />
      </IconButton>
      <form
        autoComplete="none"
        onSubmit={formik.handleSubmit}
        className={classes.form}
      >
        <Box
          sx={{
            width: "100%",
            height: "fit-content",
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            flexWrap: "wrap",
            mb: "8px",
            [theme.breakpoints.down("md")]: {
              justifyContent: "center",
            },
          }}
        >
          <Box
            sx={{
              width: "calc(33.33% - 16px)",
              height: "105px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              [theme.breakpoints.down("md")]: {
                width: "100%",
                height: "98px",
              },
            }}
          >
            <Typography
              sx={{
                width: "100%",
                padding: "8px 8px",
                lineHeight: "1",
                fontSize: "1rem",
                opacity: "0.5",
                [theme.breakpoints.down("md")]: {
                  fontSize: "14px",
                },
              }}
            >
              Nickname
            </Typography>

            <TextField
              autoComplete="new-password"
              role="textbox"
              data-testid="nickname_input"
              name="nickname"
              disabled={isDisabled}
              type={"text"}
              value={formik.values.nickname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.nickname && Boolean(formik.errors.nickname)}
              size={"small"}
              sx={{
                label: { color: (theme) => theme.palette.text.secondary },
                input: {
                  padding: "8px",
                  color: (theme) => theme.palette.text.secondary,
                  "&::placeholder": {
                    fontSize: "14px",
                    color: (theme) => theme.palette.text.secondary,
                  },
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                    transition:
                      " background-color 600000s 0s, color 600000s 0s",
                  },
                  [theme.breakpoints.down("md")]: {
                    padding: "6px",
                  },
                },
                width: "100%",
              }}
              className={classes.field}
            />

            <Typography
              sx={{
                fontSize: "14px",
                color: "#E36464",
                my: "8px",
                [theme.breakpoints.down("md")]: {
                  my: "6px",
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "12px",
                },
              }}
            >
              {formik.touched.nickname &&
                Boolean(formik.errors.nickname) &&
                formik.errors.nickname}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "calc(33.33% - 16px)",
              height: "105px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              [theme.breakpoints.up("md")]: {
                ml: "20px",
              },
              [theme.breakpoints.down("md")]: {
                width: "100%",
                height: "98px",
              },
            }}
          >
            <Typography
              sx={{
                width: "100%",
                padding: "8px 8px",
                lineHeight: "1",
                fontSize: "1rem",
                opacity: "0.5",
                [theme.breakpoints.down("md")]: {
                  fontSize: "14px",
                },
              }}
            >
              Username
            </Typography>

            <TextField
              autoComplete="new-password"
              data-testid="username_input"
              name="username"
              type={"text"}
              disabled={type === "update" ? true : false}
              value={formik.values.username}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.username && Boolean(formik.errors.username)}
              size={"small"}
              sx={{
                label: { color: (theme) => theme.palette.text.secondary },
                input: {
                  padding: "8px",
                  color: (theme) => theme.palette.text.secondary,
                  "&::placeholder": {
                    fontSize: "14px",
                    color: (theme) => theme.palette.text.secondary,
                  },
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                    transition:
                      " background-color 600000s 0s, color 600000s 0s",
                  },
                  [theme.breakpoints.down("md")]: {
                    padding: "6px",
                  },
                },
                width: "100%",
              }}
              className={classes.field}
            />

            <Typography
              sx={{
                fontSize: "14px",
                color: "#E36464",
                my: "8px",
                [theme.breakpoints.down("md")]: {
                  my: "6px",
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "12px",
                },
              }}
            >
              {formik.touched.username &&
                Boolean(formik.errors.username) &&
                formik.errors.username}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "calc(33.33% - 16px)",
              height: "105px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              [theme.breakpoints.up("md")]: {
                ml: "20px",
              },
              [theme.breakpoints.down("md")]: {
                width: "100%",
                height: "98px",
              },
            }}
          >
            <Typography
              sx={{
                width: "100%",
                padding: "8px 8px",
                lineHeight: "1",
                fontSize: "1rem",
                opacity: "0.5",
                [theme.breakpoints.down("md")]: {
                  fontSize: "14px",
                },
              }}
            >
              Role
            </Typography>

            <TextField
              data-testid="rule_input"
              name="rule"
              select
              defaultValue={"user"}
              disabled={isDisabled}
              value={formik.values.rule}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.rule && Boolean(formik.errors.rule)}
              size={"small"}
              sx={{
                label: { color: (theme) => theme.palette.text.secondary },
                input: {
                  padding: "8px",
                  color: (theme) => theme.palette.text.secondary,
                  "&::placeholder": {
                    fontSize: "14px",
                    color: (theme) => theme.palette.text.secondary,
                  },

                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                    transition:
                      " background-color 600000s 0s, color 600000s 0s",
                  },
                  [theme.breakpoints.down("md")]: {
                    padding: "6px",
                  },
                },
                "& .MuiInputBase-root": {
                  color: (theme) => theme.palette.text.secondary,
                },
                width: "100%",
              }}
              className={classes.field}
            >
              <MenuItem
                sx={{ color: (theme) => theme.palette.text.secondary }}
                key={"user"}
                value={"user"}
              >
                {"user"}
              </MenuItem>
              <MenuItem
                sx={{ color: (theme) => theme.palette.text.secondary }}
                key={"admin"}
                value={"admin"}
              >
                {"admin"}
              </MenuItem>
            </TextField>

            <Typography
              sx={{
                fontSize: "14px",
                color: "#E36464",
                my: "8px",
                [theme.breakpoints.down("md")]: {
                  my: "6px",
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "12px",
                },
              }}
            >
              {formik.touched.rule &&
                Boolean(formik.errors.rule) &&
                formik.errors.rule}
            </Typography>
          </Box>

          {type === "update" && username === formik.values.username && (
            <Box
              sx={{
                width: "calc(33.33% - 16px)",
                height: "105px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                [theme.breakpoints.down("md")]: {
                  width: "100%",
                  height: "98px",
                },
              }}
            >
              <Typography
                sx={{
                  width: "100%",
                  padding: "8px 8px",
                  lineHeight: "1",
                  fontSize: "1rem",
                  opacity: "0.5",
                  [theme.breakpoints.down("md")]: {
                    fontSize: "14px",
                  },
                }}
              >
                Old Password
              </Typography>

              <TextField
                autoComplete="new-password"
                data-testid="oldPass_input"
                name="oldPass"
                type={showPass.oldPass ? "text" : "password"}
                value={formik.values.oldPass}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.oldPass && Boolean(formik.errors.oldPass)}
                size={"small"}
                sx={{
                  label: { color: (theme) => theme.palette.text.secondary },
                  input: {
                    padding: "8px",
                    color: (theme) => theme.palette.text.secondary,
                    "&::placeholder": {
                      fontSize: "14px",
                      color: (theme) => theme.palette.text.secondary,
                    },
                    "&:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                      WebkitTextFillColor: (theme) =>
                        theme.palette.text.secondary,
                      transition:
                        " background-color 600000s 0s, color 600000s 0s",
                    },
                    [theme.breakpoints.down("md")]: {
                      padding: "6px",
                    },
                  },
                  width: "100%",
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ marginLeft: "14px", marginRight: "14px" }}
                    >
                      <SvgIcon
                        component={
                          showPass.oldPass ? VisibilityOffIcon : VisibilityIcon
                        }
                        inheritViewBox
                        sx={{
                          width: "20px",
                          cursor: "pointer",
                          color: theme.palette.text.secondary,
                          opacity: 0.5,
                        }}
                        onClick={() => {
                          setShowPass((last) => ({
                            ...last,
                            oldPass: !last.oldPass,
                          }));
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                className={classes.field}
              />

              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#E36464",
                  my: "8px",
                  [theme.breakpoints.down("md")]: {
                    my: "6px",
                  },
                  [theme.breakpoints.down("sm")]: {
                    fontSize: "12px",
                  },
                }}
              >
                {formik.touched.oldPass &&
                  Boolean(formik.errors.oldPass) &&
                  formik.errors.oldPass}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              width: "calc(33.33% - 16px)",
              height: "105px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              [theme.breakpoints.down("md")]: {
                width: "100%",
                height: "98px",
              },
              [theme.breakpoints.up("md")]: {
                ml:
                  type === "update" && username === formik.values.username
                    ? "20px"
                    : "0px",
              },
            }}
          >
            <Typography
              sx={{
                width: "100%",
                padding: "8px 8px",
                lineHeight: "1",
                fontSize: "1rem",
                opacity: "0.5",
                [theme.breakpoints.down("md")]: {
                  fontSize: "14px",
                },
              }}
            >
              New Password
            </Typography>

            <TextField
              autoComplete="new-password"
              data-testid="password_input"
              name="password"
              type={showPass.password ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              size={"small"}
              sx={{
                label: { color: (theme) => theme.palette.text.secondary },
                input: {
                  padding: "8px",
                  color: (theme) => theme.palette.text.secondary,
                  "&::placeholder": {
                    fontSize: "14px",
                    color: (theme) => theme.palette.text.secondary,
                  },
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                    transition:
                      " background-color 600000s 0s, color 600000s 0s",
                  },
                  [theme.breakpoints.down("md")]: {
                    padding: "6px",
                  },
                },
                width: "100%",
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ marginLeft: "14px", marginRight: "14px" }}
                  >
                    <SvgIcon
                      component={
                        showPass.password ? VisibilityOffIcon : VisibilityIcon
                      }
                      inheritViewBox
                      sx={{
                        width: "20px",
                        cursor: "pointer",
                        color: theme.palette.text.secondary,
                        opacity: 0.5,
                      }}
                      onClick={() => {
                        setShowPass((last) => ({
                          ...last,
                          password: !last.password,
                        }));
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              className={classes.field}
            />

            <Typography
              sx={{
                fontSize: "14px",
                color: "#E36464",
                my: "8px",
                [theme.breakpoints.down("md")]: {
                  my: "6px",
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "12px",
                },
              }}
            >
              {formik.touched.password &&
                Boolean(formik.errors.password) &&
                formik.errors.password}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "calc(33.33% - 16px)",
              height: "105px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              [theme.breakpoints.up("md")]: {
                ml: "20px",
              },
              [theme.breakpoints.down("md")]: {
                width: "100%",
                height: "98px",
              },
            }}
          >
            <Typography
              sx={{
                width: "100%",
                padding: "8px 8px",
                lineHeight: "1",
                fontSize: "1rem",
                opacity: "0.5",
                [theme.breakpoints.down("md")]: {
                  fontSize: "14px",
                },
              }}
            >
              Confirm Password
            </Typography>

            <TextField
              autoComplete="new-password"
              data-testid="passwordConf_input"
              name="passwordConf"
              type={showPass.passwordConf ? "text" : "password"}
              value={formik.values.passwordConf}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.passwordConf &&
                Boolean(formik.errors.passwordConf)
              }
              size={"small"}
              sx={{
                label: { color: (theme) => theme.palette.text.secondary },
                input: {
                  padding: "8px",
                  color: (theme) => theme.palette.text.secondary,
                  "&::placeholder": {
                    fontSize: "14px",
                    color: (theme) => theme.palette.text.secondary,
                  },
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                    transition:
                      " background-color 600000s 0s, color 600000s 0s",
                  },
                  [theme.breakpoints.down("md")]: {
                    padding: "6px",
                  },
                },
                width: "100%",
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ marginLeft: "14px", marginRight: "14px" }}
                  >
                    <SvgIcon
                      component={
                        showPass.passwordConf
                          ? VisibilityOffIcon
                          : VisibilityIcon
                      }
                      inheritViewBox
                      sx={{
                        width: "20px",
                        cursor: "pointer",
                        color: theme.palette.text.secondary,
                        opacity: 0.5,
                      }}
                      onClick={() => {
                        setShowPass((last) => ({
                          ...last,
                          passwordConf: !last.passwordConf,
                        }));
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              className={classes.field}
            />

            <Typography
              sx={{
                fontSize: "14px",
                color: "#E36464",
                my: "8px",
                [theme.breakpoints.down("md")]: {
                  my: "6px",
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "12px",
                },
              }}
            >
              {formik.touched.passwordConf &&
                Boolean(formik.errors.passwordConf) &&
                formik.errors.passwordConf}
            </Typography>
          </Box>
        </Box>

        {openButton && (
          <Box
            sx={{
              width: "100%",
              height: openButton ? "fit-content" : "0px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: "8px",
            }}
          >
            <Fade in={openButton} timeout={500}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: "24px",
                }}
              >
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  sx={{
                    width: "100px",
                    height: "40px",
                    fontSize: "18px",
                    borderRadius: "8px",
                    textTransform: "none",
                    lineHeight: "0px",
                    padding: "12px",
                    mr: "8px",
                    color: theme.palette.text.secondary,
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    "&:hover": {
                      color: theme.palette.text.primary,
                      transition: "all 0.4s",
                    },
                    [theme.breakpoints.down("md")]: {
                      width: "calc(50% - 4px)",
                    },
                  }}
                  onClick={() => {
                    formik.setValues({
                      nickname: "",
                      username: "",
                      rule: "user",
                      password: "",
                      passwordConf: "",
                      oldPass: "",
                    });
                    formik.setTouched({});
                    dispatch(resetCreateUserAction());
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  type="submit"
                  disabled={
                    isDisabled
                      ? Object.keys(formik.touched).length > 0 &&
                        Object.keys(formik.errors).length === 0
                        ? false
                        : true
                      : Object.keys(formik.touched).length > 0 &&
                        Object.keys(formik.errors).length === 0
                      ? false
                      : true
                  }
                  sx={{
                    width: "100px",
                    height: "40px",
                    fontSize: "18px",
                    borderRadius: "8px",
                    textTransform: "none",
                    lineHeight: "0px",
                    padding: "12px",
                    transition: "all 0.4s",
                    ":disabled": {
                      color: theme.palette.success.contrastText,
                      boxShadow: "none",
                      backgroundColor: theme.palette.success.main,
                      opacity: "0.3",
                    },
                    [theme.breakpoints.down("md")]: {
                      width: "calc(50% - 4px)",
                    },
                  }}
                >
                  Save
                </Button>
              </Box>
            </Fade>
          </Box>
        )}
      </form>
    </Paper>
  );
};

export default CreateUserForm;
